import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiFetch } from "@/lib/api/client";
import { useAuth } from "@/contexts/AuthContext";
import type { ContractTypeRecord, CustomRoleRecord } from "@/types";

export type RefreshContractTypesOptions = {
  /** Refetch without setting isLoading — keeps the list visible during update. */
  background?: boolean;
};

export type RefreshCustomRolesOptions = {
  /** Refetch without setting isLoading — keeps the list visible during update. */
  background?: boolean;
};

interface ContractTypesContextValue {
  contractTypes: ContractTypeRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: (options?: RefreshContractTypesOptions) => Promise<void>;
  upsertUserType: (payload: {
    id?: string;
    name: string;
    value: string;
    guidelines: string;
    omit_document_ids?: string[];
  }) => Promise<string>;
  deleteUserType: (id: string) => Promise<void>;
}

interface CustomRolesContextValue {
  roles: CustomRoleRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: (options?: RefreshCustomRolesOptions) => Promise<void>;
  upsertUserRole: (payload: {
    id?: string;
    name: string;
    role_key: string;
    instructions: string;
    omit_document_ids?: string[];
  }) => Promise<string>;
  deleteUserRole: (id: string) => Promise<void>;
}

const ContractTypesContext = createContext<ContractTypesContextValue | undefined>(undefined);
const CustomRolesContext = createContext<CustomRolesContextValue | undefined>(undefined);

export function ReferenceDataProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  const [contractTypes, setContractTypes] = useState<ContractTypeRecord[]>([]);
  const [contractTypesLoading, setContractTypesLoading] = useState(false);
  const [contractTypesError, setContractTypesError] = useState<string | null>(null);

  const [roles, setRoles] = useState<CustomRoleRecord[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const refreshContractTypes = useCallback(
    async (options?: RefreshContractTypesOptions) => {
      if (!user) {
        setContractTypes([]);
        setContractTypesLoading(false);
        return;
      }
      const background = options?.background === true;
      if (!background) {
        setContractTypesLoading(true);
      }
      setContractTypesError(null);
      try {
        const data = await apiFetch<ContractTypeRecord[]>("/api/contract-types");
        setContractTypes(data);
      } catch (err) {
        setContractTypesError(err instanceof Error ? err.message : "Failed to load contract types");
      } finally {
        if (!background) {
          setContractTypesLoading(false);
        }
      }
    },
    [user]
  );

  const refreshRoles = useCallback(
    async (options?: RefreshCustomRolesOptions) => {
      if (!user) {
        setRoles([]);
        setRolesLoading(false);
        return;
      }
      const background = options?.background === true;
      if (!background) {
        setRolesLoading(true);
      }
      setRolesError(null);
      try {
        const data = await apiFetch<CustomRoleRecord[]>("/api/custom-roles");
        setRoles(data);
      } catch (err) {
        setRolesError(err instanceof Error ? err.message : "Failed to load roles");
      } finally {
        if (!background) {
          setRolesLoading(false);
        }
      }
    },
    [user]
  );

  useEffect(() => {
    void refreshContractTypes();
  }, [refreshContractTypes]);

  useEffect(() => {
    void refreshRoles();
  }, [refreshRoles]);

  const upsertUserType = useCallback(
    async (payload: {
      id?: string;
      name: string;
      value: string;
      guidelines: string;
      omit_document_ids?: string[];
    }) => {
      if (!user) throw new Error("User not authenticated");

      if (payload.id) {
        await apiFetch(`/api/contract-types/${payload.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/api/contract-types", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      await refreshContractTypes();
      const updated = await apiFetch<ContractTypeRecord[]>("/api/contract-types");
      const match = updated.find((t) => t.value === payload.value && t.user_id === user.id);
      return match?.id ?? payload.id ?? "";
    },
    [refreshContractTypes, user]
  );

  const deleteUserType = useCallback(
    async (id: string) => {
      if (!user) throw new Error("User not authenticated");
      await apiFetch(`/api/contract-types/${id}`, { method: "DELETE" });
      await refreshContractTypes();
    },
    [refreshContractTypes, user]
  );

  const upsertUserRole = useCallback(
    async (payload: {
      id?: string;
      name: string;
      role_key: string;
      instructions: string;
      omit_document_ids?: string[];
    }) => {
      if (!user) throw new Error("User not authenticated");

      if (payload.id) {
        await apiFetch(`/api/custom-roles/${payload.id}`, {
          method: "PATCH",
          body: JSON.stringify(payload),
        });
      } else {
        await apiFetch("/api/custom-roles", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      }
      await refreshRoles();
      const updated = await apiFetch<CustomRoleRecord[]>("/api/custom-roles");
      const match = updated.find((r) => r.role_key === payload.role_key && r.user_id === user.id);
      return match?.id ?? payload.id ?? "";
    },
    [refreshRoles, user]
  );

  const deleteUserRole = useCallback(
    async (id: string) => {
      if (!user) throw new Error("User not authenticated");
      await apiFetch(`/api/custom-roles/${id}`, { method: "DELETE" });
      await refreshRoles();
    },
    [refreshRoles, user]
  );

  const contractTypesValue = useMemo<ContractTypesContextValue>(
    () => ({
      contractTypes,
      isLoading: contractTypesLoading,
      error: contractTypesError,
      refresh: refreshContractTypes,
      upsertUserType,
      deleteUserType,
    }),
    [contractTypes, contractTypesLoading, contractTypesError, refreshContractTypes, upsertUserType, deleteUserType]
  );

  const customRolesValue = useMemo<CustomRolesContextValue>(
    () => ({
      roles,
      isLoading: rolesLoading,
      error: rolesError,
      refresh: refreshRoles,
      upsertUserRole,
      deleteUserRole,
    }),
    [roles, rolesLoading, rolesError, refreshRoles, upsertUserRole, deleteUserRole]
  );

  return (
    <ContractTypesContext.Provider value={contractTypesValue}>
      <CustomRolesContext.Provider value={customRolesValue}>{children}</CustomRolesContext.Provider>
    </ContractTypesContext.Provider>
  );
}

export function useContractTypes() {
  const ctx = useContext(ContractTypesContext);
  if (!ctx) throw new Error("useContractTypes must be used within ReferenceDataProvider");
  return ctx;
}

export function useCustomRolesDB() {
  const ctx = useContext(CustomRolesContext);
  if (!ctx) throw new Error("useCustomRolesDB must be used within ReferenceDataProvider");
  return ctx;
}
